import { useTranslation } from "react-i18next"

import { AlertTriangle } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

// Concurrency conflict banner for the hosted-agent edit wizard.
// "revision": someone else changed the agent under a form PUT (409).
// "raw": a raw-JSON save changed the stored definition while the form still
// has unsaved edits — the admin chooses between reloading and keeping edits.
export function HostedAgentEditConflictAlert({
  conflict,
  onReload,
  onDismiss,
}: {
  conflict: "revision" | "raw"
  onReload: () => void
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  const isRevision = conflict === "revision"
  return (
    <Alert
      variant={isRevision ? "destructive" : "default"}
      className={
        isRevision
          ? "border-foreground bg-destructive text-destructive-foreground dark:border-foreground [&>svg]:text-destructive-foreground"
          : undefined
      }
    >
      <AlertTriangle className="size-4" />
      <AlertTitle>
        {isRevision
          ? t("adminDashboard.hostedAgents.editDialog.conflictTitle", {
              defaultValue: "Edit conflict",
            })
          : t("adminDashboard.hostedAgents.editDialog.rawSavedTitle", {
              defaultValue: "Raw definition saved",
            })}
      </AlertTitle>
      <AlertDescription>
        <p>
          {isRevision
            ? t("adminDashboard.hostedAgents.editDialog.conflictDescription", {
                defaultValue:
                  "The agent was changed by someone else while you were editing. Reload to get the latest version — your unsaved changes here will be discarded.",
              })
            : t("adminDashboard.hostedAgents.editDialog.rawSavedDescription", {
                defaultValue:
                  "The raw save changed the stored definition, but you still have unsaved form edits. Reload the form to see the saved version (discarding your form edits), or keep editing — saving the form later can overwrite the raw changes.",
              })}
        </p>
        <div className="mt-2 flex gap-2">
          <Button
            size="sm"
            type="button"
            variant={isRevision ? "destructive" : "default"}
            onClick={onReload}
          >
            {t("adminDashboard.hostedAgents.editDialog.conflictReload", {
              defaultValue: "Reload latest",
            })}
          </Button>
          <Button size="sm" type="button" variant="outline" onClick={onDismiss}>
            {t("adminDashboard.hostedAgents.editDialog.conflictDismiss", {
              defaultValue: "Keep editing",
            })}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  )
}
